import { Injectable, inject } from '@angular/core';
import { Observable, from, switchMap } from 'rxjs';
import { ApiService } from './api.service';
import { WorkspaceInstance, WorkspaceBookmark } from '@crowdctrl/core';

/**
 * Service for workspace instance management API calls.
 */
@Injectable({
  providedIn: 'root',
})
export class WorkspaceInstanceService {
  private apiService = inject(ApiService);
  private baseUrl: string = '';

  /**
   * Set the base URL for API calls (should be called from app initialization)
   */
  setBaseUrl(url: string): void {
    this.baseUrl = url;
  }

  /**
   * Get all workspace instances for a tenant with optional pagination.
   */
  getInstances(
    limit?: number,
    cursor?: string,
  ): Observable<{
    instances: WorkspaceInstance[];
    pagination: {
      limit: number;
      hasNext: boolean;
      nextCursor: string | null;
      totalCount: number;
      retrievedAt: string;
    };
  }> {
    const params: Record<string, string | number | boolean> = {};
    if (limit) {
      params['limit'] = limit;
    }
    if (cursor) {
      params['cursor'] = cursor;
    }
    return from(
      this.apiService.get<{
        instances: WorkspaceInstance[];
        pagination: {
          limit: number;
          hasNext: boolean;
          nextCursor: string | null;
          totalCount: number;
          retrievedAt: string;
        };
      }>(this.baseUrl, `/workspace_instances_api/api/workspace-instances`, params),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Get a single workspace instance by ID
   */
  getInstance(instanceId: string): Observable<{ instance: WorkspaceInstance }> {
    return from(
      this.apiService.get<{ instance: WorkspaceInstance }>(
        this.baseUrl,
        `/workspace_instances_api/api/workspace-instances/${instanceId}`,
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Create a new workspace instance from a definition
   */
  createInstance(instanceData: {
    workspaceDefinitionId: string;
    name: string;
    description?: string;
    customizationOptions?: Record<string, any>;
  }): Observable<{ instance: WorkspaceInstance }> {
    return from(
      this.apiService.post<
        { instance: WorkspaceInstance },
        {
          workspaceDefinitionId: string;
          name: string;
          description?: string;
          customizationOptions?: Record<string, any>;
        }
      >(this.baseUrl, `/workspace_instances_api/api/workspace-instances`, {
        ...instanceData,
      }),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Update an existing workspace instance
   */
  updateInstance(
    instanceId: string,
    instanceData: Partial<WorkspaceInstance>,
  ): Observable<{ instance: WorkspaceInstance }> {
    return from(
      this.apiService.put<{ instance: WorkspaceInstance }, Partial<WorkspaceInstance>>(
        this.baseUrl,
        `/workspace_instances_api/api/workspace-instances/${instanceId}`,
        {
          ...instanceData,
        },
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Update thumbnail URL for a workspace instance
   */
  updateThumbnail(
    instanceId: string,
    thumbnailUrl: string | null,
  ): Observable<{ instance: WorkspaceInstance }> {
    return this.updateInstance(instanceId, {
      thumbnailUrl: thumbnailUrl || undefined,
    });
  }

  /**
   * Delete a workspace instance.
   * Tenant is inferred server-side from the user's auth token.
   */
  deleteInstance(instanceId: string): Observable<{ success: boolean }> {
    return from(
      this.apiService.delete<{ success: boolean }>(
        this.baseUrl,
        `/workspace_instances_api/api/workspace-instances/${instanceId}`,
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Delete all orders and all order-related stats/counts for the workspace.
   * Tenant is inferred server-side from the user's auth token.
   */
  deleteOrdersAndStats(instanceId: string): Observable<{ success: boolean }> {
    return from(
      this.apiService.delete<{ success: boolean }>(
        this.baseUrl,
        `/workspace_instances_api/api/workspace-instances/${instanceId}/orders-and-stats`,
      ),
    ).pipe(switchMap((obs) => obs));
  }

  listQueueInstances(
    instanceId: string,
    limit?: number,
    cursor?: string,
  ): Observable<{
    items: WorkspaceInstance['queueInstances'];
    pagination: {
      limit: number;
      hasNext: boolean;
      nextCursor: string | null;
      totalCount: number;
      retrievedAt: string;
    };
  }> {
    const params: Record<string, string | number | boolean> = {};
    if (limit) params['limit'] = limit;
    if (cursor) params['cursor'] = cursor;
    return from(
      this.apiService.get<{
        items: WorkspaceInstance['queueInstances'];
        pagination: {
          limit: number;
          hasNext: boolean;
          nextCursor: string | null;
          totalCount: number;
          retrievedAt: string;
        };
      }>(
        this.baseUrl,
        `/workspace_instances_api/api/workspace-instances/${instanceId}/queues`,
        params,
      ),
    ).pipe(switchMap((obs) => obs));
  }

  getQueueInstance(
    instanceId: string,
    queueId: string,
  ): Observable<{ queue: WorkspaceInstance['queueInstances'][number] }> {
    return from(
      this.apiService.get<{
        queue: WorkspaceInstance['queueInstances'][number];
      }>(
        this.baseUrl,
        `/workspace_instances_api/api/workspace-instances/${instanceId}/queues/${queueId}`,
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Get order counts for a workspace instance, optionally filtered by queue.
   * staleByStatus and recomputeScheduledAtByStatus support per-row spinners.
   * When scheduleRecompute is true, backend enqueues a recompute (retry); pass status to retry only that status.
   * recomputeFailedAtByStatus and recomputeErrorByStatus are per-status so each stat can show its own Retry.
   */
  getOrderCounts(
    instanceId: string,
    queueId?: string,
    scheduleRecompute?: boolean,
    status?: string,
  ): Observable<{
    counts: Record<string, number>;
    orderFlow?: {
      stages: Array<{ key: string; label: string; count: number }>;
      total: number;
      maxCount: number;
    };
    retrievedAt: string;
    cacheUpdatedAt?: string;
    staleByStatus?: Record<string, boolean>;
    recomputeScheduledAtByStatus?: Record<string, string>;
    recomputeFailedAtByStatus?: Record<string, string>;
    recomputeErrorByStatus?: Record<string, string>;
  }> {
    const params: Record<string, string> = {};
    if (queueId) {
      params['queueId'] = queueId;
    }
    if (scheduleRecompute === true) {
      params['scheduleRecompute'] = 'true';
    }
    if (status) {
      params['status'] = status;
    }
    return from(
      this.apiService.get<{
        counts: Record<string, number>;
        orderFlow?: {
          stages: Array<{ key: string; label: string; count: number }>;
          total: number;
          maxCount: number;
        };
        retrievedAt: string;
        cacheUpdatedAt?: string;
        staleByStatus?: Record<string, boolean>;
        recomputeScheduledAtByStatus?: Record<string, string>;
        recomputeFailedAtByStatus?: Record<string, string>;
        recomputeErrorByStatus?: Record<string, string>;
      }>(
        this.baseUrl,
        `/workspace_instances_api/api/workspace-instances/${instanceId}/order-counts`,
        params,
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Get order counts per session for a specific status in a workspace instance.
   */
  getOrderCountsByStatus(
    instanceId: string,
    status: string,
    limit?: number,
    cursor?: string,
  ): Observable<{
    sessions: Array<{ sessionId: string; sessionName: string; count: number }>;
    pagination: {
      limit: number;
      hasNext: boolean;
      nextCursor: string | null;
      totalCount: number;
      retrievedAt: string;
    };
  }> {
    const params: Record<string, string | number> = {};
    if (limit) {
      params['limit'] = limit;
    }
    if (cursor) {
      params['cursor'] = cursor;
    }
    return from(
      this.apiService.get<{
        sessions: Array<{
          sessionId: string;
          sessionName: string;
          count: number;
        }>;
        pagination: {
          limit: number;
          hasNext: boolean;
          nextCursor: string | null;
          totalCount: number;
          retrievedAt: string;
        };
      }>(
        this.baseUrl,
        `/workspace_instances_api/api/workspace-instances/${instanceId}/order-counts/${status}`,
        params,
      ),
    ).pipe(switchMap((obs) => obs));
  }

  listFormInstances(
    instanceId: string,
    limit?: number,
    cursor?: string,
  ): Observable<{
    items: WorkspaceInstance['formInstances'];
    pagination: {
      limit: number;
      hasNext: boolean;
      nextCursor: string | null;
      totalCount: number;
      retrievedAt: string;
    };
  }> {
    const params: Record<string, string | number | boolean> = {};
    if (limit) params['limit'] = limit;
    if (cursor) params['cursor'] = cursor;
    return from(
      this.apiService.get<{
        items: WorkspaceInstance['formInstances'];
        pagination: {
          limit: number;
          hasNext: boolean;
          nextCursor: string | null;
          totalCount: number;
          retrievedAt: string;
        };
      }>(
        this.baseUrl,
        `/workspace_instances_api/api/workspace-instances/${instanceId}/forms`,
        params,
      ),
    ).pipe(switchMap((obs) => obs));
  }

  listBoardInstances(
    instanceId: string,
    limit?: number,
    cursor?: string,
  ): Observable<{
    items: WorkspaceInstance['boardInstances'];
    pagination: {
      limit: number;
      hasNext: boolean;
      nextCursor: string | null;
      totalCount: number;
      retrievedAt: string;
    };
  }> {
    const params: Record<string, string | number | boolean> = {};
    if (limit) params['limit'] = limit;
    if (cursor) params['cursor'] = cursor;
    return from(
      this.apiService.get<{
        items: WorkspaceInstance['boardInstances'];
        pagination: {
          limit: number;
          hasNext: boolean;
          nextCursor: string | null;
          totalCount: number;
          retrievedAt: string;
        };
      }>(
        this.baseUrl,
        `/workspace_instances_api/api/workspace-instances/${instanceId}/boards`,
        params,
      ),
    ).pipe(switchMap((obs) => obs));
  }

  listBookmarks(limit?: number): Observable<{
    items: WorkspaceBookmark[];
    pagination: {
      limit: number;
      totalCount: number;
      retrievedAt: string;
    };
  }> {
    const params: Record<string, string | number | boolean> = {};
    if (limit) params['limit'] = limit;
    return from(
      this.apiService.get<{
        items: WorkspaceBookmark[];
        pagination: {
          limit: number;
          totalCount: number;
          retrievedAt: string;
        };
      }>(this.baseUrl, `/workspace_instances_api/api/workspace-bookmarks`, params),
    ).pipe(switchMap((obs) => obs));
  }

  createBookmark(instanceId: string): Observable<{ bookmarkId: string }> {
    return from(
      this.apiService.post<{ bookmarkId: string }, Record<string, never>>(
        this.baseUrl,
        `/workspace_instances_api/api/workspace-instances/${instanceId}/bookmark`,
        {},
      ),
    ).pipe(switchMap((obs) => obs));
  }

  removeBookmark(instanceId: string): Observable<{ success: boolean }> {
    return from(
      this.apiService.delete<{ success: boolean }>(
        this.baseUrl,
        `/workspace_instances_api/api/workspace-instances/${instanceId}/bookmark`,
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Create a workspace-level structure (queue, form, or board).
   */
  createStructure(
    instanceId: string,
    structureType: 'queues' | 'forms' | 'boards',
    structureData: {
      name: string;
      description?: string;
      capacity?: number;
      settings?: Record<string, any>;
      [key: string]: any;
    },
  ): Observable<{ structure: any }> {
    return from(
      this.apiService.post<
        { structure: any },
        {
          name: string;
          description?: string;
          capacity?: number;
          settings?: Record<string, any>;
          [key: string]: any;
        }
      >(
        this.baseUrl,
        `/workspace_instances_api/api/workspace-instances/${instanceId}/${structureType}`,
        structureData,
      ),
    ).pipe(switchMap((obs) => obs));
  }
}
